import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Container, Grid, Typography, Box, Card, CardContent, TextField, MenuItem, FormControl, InputLabel, Select, SelectChangeEvent } from '@mui/material';
import { News, Team, League } from '../types';
import { ApiErrorResponse } from '../types/errors';
import newsService from '../services/newsService';
import teamService from '../services/teamService';
import leagueService from '../services/leagueService';
import { useApiError } from '../hooks/useApiError';
import LoadingDisplay from '../components/common/LoadingDisplay';
import ErrorDisplay from '../components/common/ErrorDisplay';

const NewsPage: React.FC = () => {
  const { t } = useTranslation();
  const { handleError } = useApiError();
  
  // State for data
  const [news, setNews] = useState<News[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [leagues, setLeagues] = useState<League[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // State for filters
  const [selectedTeam, setSelectedTeam] = useState<number | ''>('');
  const [selectedLeague, setSelectedLeague] = useState<number | ''>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [dateRange, setDateRange] = useState({
    start: '',
    end: ''
  });

  // Fetch initial data only once on mount
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setLoading(true);
        const [newsResponse, teamsResponse, leaguesResponse] = await Promise.all([
          newsService.getAllNews(),
          teamService.getAllTeams(),
          leagueService.getAllLeagues()
        ]);
        
        setNews(newsResponse.results);
        setTeams(teamsResponse.results);
        setLeagues(leaguesResponse.results);
      } catch (err) {
        const apiError: ApiErrorResponse = {
          message: err instanceof Error ? err.message : 'Unknown error',
          code: 'UNKNOWN',
          originalError: err
        };
        handleError(apiError);
        setError(t('errors.fetchDataFailed'));
      } finally {
        setLoading(false);
      }
    };

    fetchInitialData();
  }, [handleError, t]);

  // Filter news based on selected filters
  useEffect(() => {
    const filterNews = async () => {
      try {
        setLoading(true);
        let filteredNews: News[] = [];

        if (selectedTeam) {
          const response = await newsService.getNewsByTeam(selectedTeam);
          filteredNews = Array.isArray(response) ? response : [];
        } else if (selectedLeague) {
          const response = await newsService.getNewsByLeague(selectedLeague);
          filteredNews = Array.isArray(response) ? response : [];
        } else if (searchQuery) {
          const response = await newsService.searchNews(searchQuery);
          filteredNews = Array.isArray(response) ? response : [];
        } else if (dateRange.start && dateRange.end) {
          const response = await newsService.getNewsByDateRange(dateRange.start, dateRange.end);
          filteredNews = Array.isArray(response) ? response : [];
        } else {
          const response = await newsService.getAllNews();
          filteredNews = response.results;
        }

        setNews(filteredNews);
      } catch (err) {
        const apiError: ApiErrorResponse = {
          message: err instanceof Error ? err.message : 'Unknown error',
          code: 'UNKNOWN',
          originalError: err
        };
        handleError(apiError);
        setError(t('errors.fetchNewsFailed'));
      } finally {
        setLoading(false);
      }
    };

    filterNews();
  }, [selectedTeam, selectedLeague, searchQuery, dateRange, handleError, t]);

  const handleTeamChange = (event: SelectChangeEvent<number>) => {
    setSelectedTeam(event.target.value as number);
    setSelectedLeague('');
    setSearchQuery('');
    setDateRange({ start: '', end: '' });
  };

  const handleLeagueChange = (event: SelectChangeEvent<number>) => {
    setSelectedLeague(event.target.value as number);
    setSelectedTeam('');
    setSearchQuery('');
    setDateRange({ start: '', end: '' });
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
    setSelectedTeam('');
    setSelectedLeague('');
    setDateRange({ start: '', end: '' });
  };

  const handleDateChange = (field: 'start' | 'end') => (event: React.ChangeEvent<HTMLInputElement>) => {
    setDateRange(prev => ({
      ...prev,
      [field]: event.target.value
    }));
    setSelectedTeam('');
    setSelectedLeague('');
    setSearchQuery('');
  };

  if (loading) {
    return <LoadingDisplay />;
  }

  if (error) {
    return <ErrorDisplay message={error} />;
  }

  return (
    <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
      <Typography
        variant="h2"
        component="h1"
        gutterBottom
        align="center"
        sx={{
          fontFamily: '"Playfair Display", serif',
          mb: { xs: 2, sm: 3, md: 4 },
          borderBottom: '2px solid',
          borderColor: 'primary.main',
          pb: { xs: 1, sm: 2 },
        }}
      >
        {t('news.title')}
      </Typography>

      <Grid container spacing={3}>
        {/* Filters */}
        <Grid item xs={12}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth>
                    <InputLabel>{t('news.filterByTeam')}</InputLabel>
                    <Select
                      value={selectedTeam}
                      label={t('news.filterByTeam')}
                      onChange={handleTeamChange}
                    >
                      {teams.map((team) => (
                        <MenuItem key={team.id} value={team.id}>
                          {team.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth>
                    <InputLabel>{t('news.filterByLeague')}</InputLabel>
                    <Select
                      value={selectedLeague}
                      label={t('news.filterByLeague')}
                      onChange={handleLeagueChange}
                    >
                      {leagues.map((league) => (
                        <MenuItem key={league.id} value={league.id}>
                          {league.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label={t('news.search')}
                    value={searchQuery}
                    onChange={handleSearchChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    type="date"
                    label={t('news.startDate')}
                    value={dateRange.start}
                    onChange={handleDateChange('start')}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    type="date"
                    label={t('news.endDate')}
                    value={dateRange.end}
                    onChange={handleDateChange('end')}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* News List */}
        <Grid item xs={12}>
          {news.map((newsItem) => (
            <Card 
              key={newsItem.id} 
              sx={{ 
                mb: 2,
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: 'action.hover'
                }
              }}
              onClick={() => window.open(newsItem.url, '_blank')}
            >
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {newsItem.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {newsItem.team_name} • {newsItem.league_name} • {new Date(newsItem.created_at).toLocaleDateString()}
                </Typography>
                <Typography variant="body1">
                  {newsItem.content}
                </Typography>
                <Typography 
                  variant="body2" 
                  color="primary" 
                  sx={{ mt: 1 }}
                >
                  {t('news.readMore')}
                </Typography>
              </CardContent>
            </Card>
          ))}
        </Grid>
      </Grid>
    </Container>
  );
};

export default NewsPage; 